import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiZap, FiBook, FiTool } from 'react-icons/fi';
import ScrollToTop from '../utils/ScrollToTop';
import { toolQuestions } from '../data/toolQuestions';

const coreQuestions = [
  {
    id: 1,
    type: "mode",
    text: "Du siehst eine komplexe Textaufgabe. Was tust du in den ersten 10 Sekunden?",
    options: [
      { text: "Ich markiere sofort alle Aufgabenoperatoren (Signalwörter), gegebenen und gesuchten Größen.", trait: "structure" },
      { text: "Ich fange an, die ersten Werte in den Taschenrechner zu tippen.", trait: "speed" },
      { text: "Ich überlege, ob der Lehrer hier eine Falle eingebaut hat.", trait: "analysis" },
      { text: "Ich versuche den Sachverhalt zu überblicken und warte auf einen Geistesblitz zum Ansatz.", trait: "intuition" },
    ],
  },
  {
    id: 2,
    type: "mode",
    text: "Du steckst mitten in einer Rechnung fest. Nichts klappt. Deine Reaktion?",
    options: [
      { text: "Ich suche systematisch in der Formelsammlung nach einem Ausweg.", trait: "structure" },
      { text: "Ich suche Zeile für Zeile nach dem Vorzeichenfehler.", trait: "analysis" },
      { text: "Ich probiere schnell verschiedene Formeln aus.", trait: "speed" },
      { text: "Ich streiche alles durch und fange intuitiv neu an.", trait: "intuition" },
    ],
  },
  {
    id: 3,
    type: "math",
    text: "Ein neues, abstraktes Thema wird eingeführt. Was hilft dir am ehesten?",
    options: [
      { text: "Eine veranschaulichende Skizze oder Übersicht sehen.", trait: "visual" },
      { text: "Drei einfache Beispielaufgaben sofort selbst rechnen.", trait: "calculating" },
      { text: "Die formalen Definitionen, korrespondierenden Sätze, Beweise und Herleitungen verstehen.", trait: "explaining" },
    ],
  },
  {
    id: 4,
    type: "math",
    text: "Wie erklärst du einem Mitschüler eine schwierige Aufgabe?",
    options: [
      { text: "Ich zeichne ein Diagramm oder eine Skizze dazu.", trait: "visual" },
      { text: "Ich rechne ihm den Weg Schritt für Schritt vor.", trait: "calculating" },
      { text: "Ich erkläre das logische Prinzip dahinter.", trait: "explaining" },
    ],
  },
];

const allQuestions = [...coreQuestions, ...toolQuestions];

const phaseLabel = {
  mode: { label: 'Denkmodus-Check', icon: FiZap },
  math: { label: 'Mathe-Lerntyp', icon: FiBook },
  tool: { label: 'Tool-Präferenz', icon: FiTool },
};

const phaseColors = {
  mode: 'from-blue-500 to-violet-500',
  math: 'from-violet-500 to-fuchsia-500',
  tool: 'from-emerald-500 to-cyan-500',
};

export default function Quiz({ onComplete }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [scores, setScores] = useState({
    mode: { structure: 0, speed: 0, analysis: 0, intuition: 0 },
    math: { visual: 0, calculating: 0, explaining: 0 },
    tool: { visual_media: 0, interactive: 0, systematic: 0, repetitive: 0 },
  });

  const handleAnswer = (trait) => {
    const q = allQuestions[currentQ];
    const newScores = { ...scores };
    newScores[q.type][trait] += 1;
    setScores(newScores);

    if (currentQ < allQuestions.length - 1) {
      setCurrentQ((prev) => prev + 1);
    } else {
      onComplete(newScores);
    }
  };

  const q = allQuestions[currentQ];
  const progress = ((currentQ + 1) / allQuestions.length) * 100;
  const phase = phaseLabel[q.type] || phaseLabel.mode;

  return (
    <div className="w-full max-w-2xl mx-auto pt-12">
      <ScrollToTop />

      <div className="mb-8">
        <div className="flex justify-between text-sm text-slate-400 mb-2">
          <span className="flex items-center gap-2">
            <SafeIcon icon={phase.icon} />
            {phase.label}
          </span>
          <span>{currentQ + 1} / {allQuestions.length}</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className={`h-full bg-gradient-to-r ${phaseColors[q.type]}`}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div className="flex justify-between mt-2">
          {['mode', 'math', 'tool'].map((p, i) => {
            const boundaries = [0, 2, 4];
            const active = currentQ >= boundaries[i];
            return (
              <span
                key={p}
                className={`text-[10px] font-black uppercase tracking-widest ${active ? 'text-slate-300' : 'text-slate-600'}`}
              >
                {['Denkmodus', 'Lerntyp', 'Tools'][i]}
              </span>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQ}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8"
        >
          {q.type === 'tool' && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black mb-4 tracking-widest uppercase">
              <SafeIcon icon={FiTool} />
              <span>Tool-Präferenz</span>
            </div>
          )}

          <h2 className="text-2xl font-bold text-white mb-8 leading-relaxed">
            {q.text}
          </h2>

          <div className="space-y-3">
            {q.options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(opt.trait)}
                className="w-full text-left p-5 rounded-2xl bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-700/50 transition-all text-slate-300 flex justify-between items-center group"
              >
                <span>{opt.text}</span>
                <SafeIcon
                  icon={FiArrowRight}
                  className="opacity-0 group-hover:opacity-100 text-blue-400 transition-all shrink-0 ml-3"
                />
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}